import { InputSummary, InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';
import { DropdownDisplayItem } from '../../data-structures/dropdown-display-item';
import {v4 as uuid} from 'uuid';
import AnessaListItemUtil, { AnessaListItem } from '../../anessa-list-item.interface';

export interface TransportationInterface {
  name: string;
  nameTranslation: string;
  uuid: string;
  isIncrease: boolean;
  transportRef: TransportationRef;
  fuelQuantity: UnitValue;
  fuelPer: UnitValue;
  fuelUsageIncrease: UnitValue;
  numVehicles: UnitValue;
  distanceChange: UnitValue;
  distancePer: UnitValue;
}

export class Transportation implements AnessaListItem {
  name: string;
  nameTranslation: string;
  uuid: string;
  isIncrease: boolean;
  transportRef: TransportationRef;
  fuelQuantity: UnitValue;
  fuelPer: UnitValue;
  fuelUsageIncrease: UnitValue;
  numVehicles: UnitValue;
  distanceChange: UnitValue;
  distancePer: UnitValue;

  constructor(param: TransportationInterface = {
    name: '',
    nameTranslation: '',
    uuid: null,
    isIncrease: false,
    transportRef: new TransportationRef(),
    fuelQuantity: new UnitValue(),
    fuelPer: new UnitValue(),
    fuelUsageIncrease: new UnitValue(),
    numVehicles: new UnitValue(),
    distanceChange: new UnitValue(),
    distancePer: new UnitValue()
  }) {
    this.name = param.name;
    this.nameTranslation = param.nameTranslation ? param.nameTranslation : param.name;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.isIncrease = param.isIncrease;
    this.transportRef = new TransportationRef(param.transportRef);
    this.fuelQuantity = new UnitValue(param.fuelQuantity);
    this.fuelPer = new UnitValue(param.fuelPer);
    this.fuelUsageIncrease = new UnitValue(param.fuelUsageIncrease);
    this.numVehicles = new UnitValue(param.numVehicles);
    this.distanceChange = new UnitValue(param.distanceChange);
    this.distancePer = new UnitValue(param.distancePer);
  }

  static deepCopy(transportation: Transportation) {
    let copy;
    if (transportation) {
      copy = new Transportation(transportation);
      return copy;
    }
    throw new Error('Transportation copy failed');
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs.push(...this.transportRef.getSummaryInputs([...path, 'transportRef']));
    inputs = InputSummaryUtils.performPush(inputs, TransportPropName.isIncrease, this.isIncrease, [...path, 'isIncrease']);
    inputs = InputSummaryUtils.performPush(inputs, TransportPropName.fuelQuantity, this.fuelQuantity, [...path, 'fuelQuantity']);
    inputs = InputSummaryUtils.performPush(inputs, TransportPropName.fuelPer, this.fuelPer, [...path, 'fuelPer']);
    inputs = InputSummaryUtils.performPush(inputs, TransportPropName.fuelUsageIncrease, this.fuelUsageIncrease, [...path, 'fuelUsageIncrease']);
    inputs = InputSummaryUtils.performPush(inputs, TransportPropName.numVehicles, this.numVehicles, [...path, 'numVehicles']);
    inputs = InputSummaryUtils.performPush(inputs, TransportPropName.distanceChange, this.distanceChange, [...path, 'distanceChange']);
    inputs = InputSummaryUtils.performPush(inputs, TransportPropName.distancePer, this.distancePer, [...path, 'distancePer']);
    return inputs;
  }

  getUuidForPath() {
    return AnessaListItemUtil.getUuidForPath(this.uuid);
  }
}

export enum TransportPropName {
  name = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATIONS.NAME',
  isIncrease = '',
  fuelQuantity = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATIONS.FUEL_QUANTITY',
  fuelPer = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATIONS.FUEL_PER',
  fuelUsageIncrease = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATIONS.FUEL_USAGE_INCREASE',
  numVehicles = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATIONS.NUM_VEHICLES',
  distanceChange = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATIONS.DISTANCE_CHANGE',
  distancePer = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATIONS.DISTANCE_PER',
  id_name = 'name',
  id_isIncrease = '',
  id_fuelQuantity = 'fuel consumed',
  id_fuelPer = 'distance per amount',
  id_fuelUsageIncrease = 'fuel usage increase',
  id_numVehicles = 'number of vehicles',
  id_distanceChange = 'distance change',
  id_distancePer = 'distance change, distance per amount'
}

export interface TransportationRefInterface {
  transportType: string;
  fuelType: string;
  vehicleType: string;
  vehicleYear: string;
  transportTranslationId: string;
  fuelTranslationId: string;
  vehicleTypeTranslationId: string;
  vehicleYearTranslationId: string;
}

export class TransportationRef {
  transportType: string;
  fuelType: string;
  vehicleType: string;
  vehicleYear: string;
  transportTranslationId: string;
  fuelTranslationId: string;
  vehicleTypeTranslationId: string;
  vehicleYearTranslationId: string;

  constructor(param: TransportationRefInterface = {
    transportType: '',
    fuelType: '',
    vehicleType: '',
    vehicleYear: '',
    transportTranslationId: '',
    fuelTranslationId: '',
    vehicleTypeTranslationId: '',
    vehicleYearTranslationId: ''
  }) {
    this.transportType = param.transportType;
    this.fuelType = param.fuelType;
    this.vehicleType = param.vehicleType;
    this.vehicleYear = param.vehicleYear;
    this.transportTranslationId = param.transportTranslationId;
    this.fuelTranslationId = param.fuelTranslationId;
    this.vehicleTypeTranslationId = param.vehicleTypeTranslationId;
    this.vehicleYearTranslationId = param.vehicleYearTranslationId;
  }

  static deepCopy(transportationRef: TransportationRef) {
    let copy;
    if (transportationRef) {
      copy = new TransportationRef(transportationRef);
      return copy;
    }
    throw new Error('Transportation ref copy failed');
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, TransportRefPropName.transportType, this.getTransportTranslationId(), [...path, 'transportType']);
    inputs = InputSummaryUtils.performPush(inputs, TransportRefPropName.fuelType, this.getFuelTranslationId(), [...path, 'fuelType']);
    inputs = InputSummaryUtils.performPush(inputs, TransportRefPropName.vehicleType, this.getVehicleTypeTranslationId(), [...path, 'vehicleType']);
    inputs = InputSummaryUtils.performPush(inputs, TransportRefPropName.vehicleYear, this.getVehicleYearTranslationId(), [...path, 'vehicleYear']);
    return inputs;
  }

  getTransportTranslationId() {
    return this.transportTranslationId ? this.transportTranslationId : this.transportType;
  }

  getFuelTranslationId() {
    return this.fuelTranslationId ? this.fuelTranslationId : this.fuelType;
  }

  getVehicleTypeTranslationId() {
    return this.vehicleTypeTranslationId ? this.vehicleTypeTranslationId : this.vehicleType;
  }

  getVehicleYearTranslationId() {
    return this.vehicleYearTranslationId ? this.vehicleYearTranslationId: this.vehicleType;
  }
}

export enum TransportRefPropName {
  transportType = 'PROJECT.GHG.TRANSPORT_TYPE',
  fuelType = 'PROJECT.GHG.FUEL_TYPE',
  vehicleType = 'PROJECT.GHG.VEHICLE_TYPE',
  vehicleYear = 'PROJECT.GHG.VEHICLE_YEAR',
}
